<template>
    <div
        :class="[isSelected ? 'border-active-elements' : 'border-ui-lines']"
        class="widget-template-item-card bg-main-bg rounded-lg border p-2 lg:p-4 cursor-pointer hover:shadow-focus-outer transition"
        data-testid="widget-template-item"
        @click="onSelectTemplate"
    >
        <div class="flex items-center gap-3">
            <div class="flex-center">
                <component
                    :is="templateIconComponent"
                    :class="[isSelected ? 'text-active-elements' : 'text-secondary-actions']"
                    class="transition w-12"
                />
            </div>
            <div class="w-44 flex-shrink-0">
                <div class="text-sm font-semibold text-default-text break-keep">
                    {{ templateName }}
                </div>
            </div>
            <div class="flex-grow">
                <div
                    v-if="usedGroups?.length"
                    class="max"
                >
                    <div class="text-xs font-semibold flex gap-2 items-center mb-1">
                        <i class="vc-icon-real-time text-sm" />
                        <span>{{ dashboardName }}</span>
                    </div>
                    <div class=" flex items-center gap-2">
                        <div class="truncate max-w-80 text-xs">
                            {{ groupsNamesInRow.join(', ') }}
                        </div>
                        <RedesignBasePopover
                            v-if="groupsNamesInOther.length"
                            trigger="hover"
                        >
                            <template #reference>
                                <span
                                    class="text-primary rounded-full border border-active-elements inline-flex items-center justify-center"
                                >
                                    <i class="vc-icon-right text-xs" />
                                </span>
                            </template>
                            <div class="space-y-1.5 text-xs max-h-96 overflow-y-auto px-1 ">
                                <div
                                    v-for="(item, i) in groupsNamesInOther"
                                    :key="i"
                                >
                                    {{ item }}
                                </div>
                            </div>
                        </RedesignBasePopover>
                    </div>
                </div>
            </div>
            <div
                v-if="isSelected"
                class="flex-center"
                @click.stop
            >
                <slot name="counter" />
            </div>
            <div
                v-else-if="hasPreview"
                class="flex-center gap-1 text-active-elements"
            >
                <RedesignBaseButton
                    type="text"
                    size="small"
                    @click.stop="onPreviewTemplate"
                >
                    <span class="inline-flex items-center">
                        <span class="text-sm">
                            {{ $t('widget.templateAbout') }}
                        </span>
                        <redesign-base-icon
                            name="ChevronRight"
                            :size="20"
                        />
                    </span>
                </RedesignBaseButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { IWidgetTemplateListItem } from '@/types/templates'
import { computed } from 'vue'
import { templateIcons } from '@/enum/widgetDataTypes'
import i18n from '@/i18n'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import { ActiveDashboardName } from '@/composables/globalData/useDashoardsData'
import { getHelpByWidgetsTemplateID } from '@/composables/globalData/useTemplatesDictionaryData'
import { TemplateHelp } from '@/types/template-help'

const props = defineProps<{
    template: Partial<IWidgetTemplateListItem>
    usedGroups?: Array<string>
    isSelected?: boolean
}>()
const emit = defineEmits<{
    (e: 'on-preview', pl: TemplateHelp): void
    (e: 'on-select', pl: Partial<IWidgetTemplateListItem>): void
}>()

const templateHelpData = computed(() => {
    return getHelpByWidgetsTemplateID(props.template.TemplateID)
})
const hasPreview = computed(() => {
    return !!templateHelpData.value
})
const templateIconComponent = computed(() => {
    const _key = props.template.DataType?.DataTypeID as keyof typeof templateIcons
    return _key !== undefined ? templateIcons[_key] || templateIcons.Default : templateIcons.Default
})
const dashboardName = computed(() => {
    return ActiveDashboardName.value
})
const templateName = computed(() => {
    return i18n.te(String(props.template.TemplateNameContentTag)) ? i18n.t(String(props.template.TemplateNameContentTag)) : props.template.TemplateName
})
const groupsNamesInRow = computed(() => {
    return props.usedGroups?.slice(0, 2) || []
})
const groupsNamesInOther = computed(() => {
    return props.usedGroups?.slice(2) || []
})

function onPreviewTemplate () {
    if (templateHelpData.value) {
        emit('on-preview', { ...templateHelpData.value })
    }

}

function onSelectTemplate () {
    emit('on-select', props.template)
}
</script>
